import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Send, Bot, User, Trash2, Sparkles, Info } from 'lucide-react';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import {
  ChatWindowWrapper,
  Header,
  HeaderContent,
  HeaderLeft,
  HeaderTitle,
  HeaderSubtitle,
  HeaderRight,
  IconButton,
  MessagesContainer,
  WelcomeMessage,
  WelcomeIcon,
  WelcomeTitle,
  WelcomeText,
  QuickQuestionsGrid,
  QuickQuestionButton,
  InputArea,
  Form,
  TextInput,
  SendButton,
  CharacterCount,
  TokenInfo,
  TokenInfoPopup,
  TokenInfoRow,
  TokenProgressBar,
  TokenProgressFill
} from './ChatWindow.styles';

const ChatWindow = ({
  messages,
  isTyping,
  onSendMessage,
  onClearChat,
  onQuickQuestion,
  quickQuestions,
  tokenUsage,
  onClose,
  messagesEndRef,
  inputRef,
  inputMessage,
  setInputMessage,
  handleSubmit
}) => {
  const [showQuickQuestions, setShowQuickQuestions] = useState(true);
  const [showTokenInfo, setShowTokenInfo] = useState(false);

  const handleQuickQuestionClick = (question) => {
    setShowQuickQuestions(false);
    onQuickQuestion(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const tokenUsagePercentage = (tokenUsage.used / tokenUsage.limit) * 100;
  const tokenColor = tokenUsagePercentage > 80 ? 'text-red-500' : 
                    tokenUsagePercentage > 60 ? 'text-yellow-500' : 'text-green-500';

  return (
    <ChatWindowWrapper>
      {/* Header */}
      <Header>
        <HeaderContent>
          <HeaderLeft>
            <WelcomeIcon>
              <Bot size={20} />
            </WelcomeIcon>
            <div>
              <HeaderTitle>AI Assistant</HeaderTitle>
              <HeaderSubtitle>Always here to help</HeaderSubtitle>
            </div>
          </HeaderLeft>
          <HeaderRight>
            {/* Token Usage Indicator */}
            <TokenInfo>
              <IconButton
                onMouseEnter={() => setShowTokenInfo(true)}
                onMouseLeave={() => setShowTokenInfo(false)}
              >
                <Info size={16} />
              </IconButton>
              
              {showTokenInfo && (
                <TokenInfoPopup
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <TokenInfoRow>
                    <span>Daily Usage:</span>
                    <span style={{ color: tokenColor }}>{tokenUsage.used.toLocaleString()}</span>
                  </TokenInfoRow>
                  <TokenInfoRow>
                    <span>Limit:</span>
                    <span>{tokenUsage.limit.toLocaleString()}</span>
                  </TokenInfoRow>
                  <TokenProgressBar>
                    <TokenProgressFill 
                      style={{ 
                        width: `${Math.min(tokenUsagePercentage, 100)}%`,
                        backgroundColor: tokenUsagePercentage > 80 ? '#ef4444' :
                                       tokenUsagePercentage > 60 ? '#f59e0b' : '#10b981'
                      }}
                    />
                  </TokenProgressBar>
                </TokenInfoPopup>
              )}
            </TokenInfo>

            <IconButton
              onClick={onClearChat}
              title="Clear chat"
            >
              <Trash2 size={16} />
            </IconButton>
            <IconButton
              onClick={onClose}
            >
              <X size={20} />
            </IconButton>
          </HeaderRight>
        </HeaderContent>
      </Header>

      {/* Messages Container */}
      <MessagesContainer>
        {messages.length === 0 && showQuickQuestions && (
          <WelcomeMessage>
            <WelcomeIcon>
              <Sparkles style={{ color: 'white' }} size={24} />
            </WelcomeIcon>
            <WelcomeTitle>Hello! How can I help you today?</WelcomeTitle>
            <WelcomeText>Choose a quick question below or type your own</WelcomeText>
            
            {/* Quick Questions */}
            <QuickQuestionsGrid>
              {quickQuestions.slice(0, 3).map((question, index) => (
                <QuickQuestionButton
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleQuickQuestionClick(question)}
                >
                  {question}
                </QuickQuestionButton>
              ))}
            </QuickQuestionsGrid>
            
            {quickQuestions.length > 3 && (
              <button
                onClick={() => setShowQuickQuestions(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: '12px',
                  marginTop: '16px',
                  cursor: 'pointer'
                }}
              >
                Type your own question →
              </button>
            )}
          </WelcomeMessage>
        )}

        {messages.map((message, index) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {isTyping && <TypingIndicator />}

        <div ref={messagesEndRef} />
      </MessagesContainer>

      {/* Input Area */}
      <InputArea>
        <Form onSubmit={handleSubmit}>
          <TextInput
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={isTyping}
            required
          />
          <SendButton
            type="submit"
            disabled={!inputMessage.trim() || isTyping}
          >
            <Send size={20} />
          </SendButton>
        </Form>
        
        {/* Character Count */}
        <CharacterCount>
          {inputMessage.length}/500
        </CharacterCount>
      </InputArea>
    </ChatWindowWrapper>
  );
};

export default ChatWindow;
